export const DEFAULT_TIME_ZONE = 'Europe/Moscow';

export function normalizeTimeZone(value, fallback = DEFAULT_TIME_ZONE) {
  const candidate = String(value || '').trim() || fallback || DEFAULT_TIME_ZONE;

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate }).format(new Date());
    return candidate;
  } catch (error) {
    return fallback && fallback !== candidate
      ? normalizeTimeZone(fallback, DEFAULT_TIME_ZONE)
      : DEFAULT_TIME_ZONE;
  }
}

export function pad(value) {
  return String(value).padStart(2, '0');
}

export function getZonedParts(date = new Date(), timeZone = DEFAULT_TIME_ZONE) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: normalizeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  if (typeof formatter.formatToParts !== 'function') {
    const fallbackDate = date instanceof Date ? date : new Date(date);
    return {
      year: fallbackDate.getFullYear(),
      month: fallbackDate.getMonth() + 1,
      day: fallbackDate.getDate(),
      hour: fallbackDate.getHours(),
      minute: fallbackDate.getMinutes(),
      second: fallbackDate.getSeconds(),
    };
  }

  const parts = formatter.formatToParts(date).reduce((acc, item) => {
    if (item.type !== 'literal') acc[item.type] = item.value;
    return acc;
  }, {});

  return {
    year: Number(parts.year || 1970),
    month: Number(parts.month || 1),
    day: Number(parts.day || 1),
    hour: Number(parts.hour || 0),
    minute: Number(parts.minute || 0),
    second: Number(parts.second || 0),
  };
}

export function getZonedDate(date = new Date(), timeZone = DEFAULT_TIME_ZONE) {
  const parts = getZonedParts(date, timeZone);
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    0
  );
}

export function getZonedDateValue(timeZone = DEFAULT_TIME_ZONE, date = new Date()) {
  const parts = getZonedParts(date, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function getZonedDateTimeValue(timeZone = DEFAULT_TIME_ZONE, date = new Date()) {
  const parts = getZonedParts(date, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)} ${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`;
}

export function getTimeZoneOffsetMinutes(timeZone = DEFAULT_TIME_ZONE, date = new Date()) {
  const parts = getZonedParts(date, timeZone);
  const zonedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return (zonedAsUtc - date.getTime()) / 60000;
}

export function zonedDateTimeToUtcMs(dateValue, timeValue = '00:00:00', timeZone = DEFAULT_TIME_ZONE) {
  const dateMatch = String(dateValue || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = String(timeValue || '00:00:00').match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);

  if (!dateMatch || !timeMatch) return null;

  const wallUtc = Date.UTC(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3]),
    Number(timeMatch[1]),
    Number(timeMatch[2]),
    Number(timeMatch[3] || 0)
  );

  let utcMs = wallUtc;
  for (let i = 0; i < 2; i += 1) {
    const offset = getTimeZoneOffsetMinutes(timeZone, new Date(utcMs));
    utcMs = wallUtc - (offset * 60000);
  }

  return utcMs;
}

export function dbDateTimeToUtcMs(value, timeZone = DEFAULT_TIME_ZONE) {
  const normalized = String(value || '').replace('T', ' ');
  const match = normalized.match(/^(\d{4}-\d{2}-\d{2}) (\d{1,2}:\d{2}(?::\d{2})?)$/);

  if (!match) return null;
  return zonedDateTimeToUtcMs(match[1], match[2], timeZone);
}
